// One creative of a campaign, as a card: what it is, what it is doing at the
// provider, and what it has delivered — the three things the creative step
// used to answer with a name and "Applied version N".
//
// Presentational. The delivery and metrics models come from
// `lib/creative-slot-view`; the caller supplies the action row, because the
// verbs differ between a draft slot (edit, remove) and a published one
// (pause, roll out, edit) and each control names its own reason when it
// cannot act.

import type { ReactNode } from 'react';
import type { CampaignSyncTarget } from '@/api/manage-types';
import { Sparkline, fmt } from '@/components/primitives';
import type {
  CreativePoster,
  SizeChip,
  SlotChangeProgress,
  SlotDelivery,
  SlotMetrics,
  SlotStepState,
  SlotTone,
} from '@/lib/creative-slot-view';
import { fmtTimeSeconds } from '@/lib/datetime';

export interface CreativeSlotCardProps {
  /** A stable DOM id (`campaign-creative-slot-{n}-card`), so the header pill can scroll here. */
  id?: string;
  name: string;
  poster: CreativePoster | null;
  initials: string;
  isVideo: boolean;
  /** "Image", "Video" */
  mediaLabel: string;
  /** The library row's format text, e.g. "fs + story" or "30s · 16:9". */
  formatLabel: string;
  /** "v1 · approved", "v1 · flying" */
  versionLabel: string;
  /** A newer approved version the slot does not fly yet. */
  newerVersion?: number | null;
  /** Meta: the three sizes, present or not. Absent for other channels. */
  sizes?: SizeChip[];
  /** A sentence under the size chips: what the missing size costs. */
  sizesNote?: string;
  provider: CampaignSyncTarget;
  delivery?: SlotDelivery | null;
  /**
   * A pause or resume in flight, or one that failed: the stepper replaces the
   * per-audience lines and the headline carries an animated indicator.
   */
  progress?: SlotChangeProgress | null;
  metrics?: SlotMetrics | null;
  /** Column heading over the metrics: "Last 23 days". */
  metricsWindow?: string;
  /** Overrides the delivery tone, for a draft card that has no delivery. */
  tone?: SlotTone;
  actions: ReactNode;
  /** Notices below the grid: a version badge, a roll-out confirmation. */
  footer?: ReactNode;
  /**
   * A draft slot has no delivery and no metrics; it shows its copy instead.
   * When set, this replaces the two right-hand columns.
   */
  aside?: ReactNode;
  ariaLabel: string;
}

const TONE_COLOR: Record<SlotTone, string> = {
  live: 'var(--live)',
  paused: 'var(--ink-3)',
  busy: 'var(--accent)',
  failed: 'var(--bad)',
  quiet: 'var(--ink-3)',
};

const STEP_GLYPH: Record<SlotStepState, string> = { done: '✓', active: '', failed: '✗', pending: '·' };

function Steps({ progress }: { progress: SlotChangeProgress }) {
  return (
    <ol className="creative-slot-steps" aria-label="Change steps">
      {progress.steps.map((step) => (
        <li key={step.key} className={`creative-slot-step creative-slot-step--${step.state}`}>
          <span className="creative-slot-step-glyph" aria-hidden="true">
            {step.state === 'active' ? <span className="creative-slot-spinner" /> : STEP_GLYPH[step.state]}
          </span>
          <span>
            {step.label}
            {step.detail && <span className="creative-slot-step-detail"> · {step.detail}</span>}
          </span>
          {step.at && <span className="creative-slot-step-at mono tnum">{fmtTimeSeconds(step.at)}</span>}
        </li>
      ))}
    </ol>
  );
}

function Metric({ label, value, big = false }: { label: string; value: string; big?: boolean }) {
  return (
    <div className="creative-slot-metric">
      <div className="creative-slot-metric-label">{label}</div>
      <div className={big ? 'creative-slot-metric-big tnum' : 'creative-slot-metric-value tnum'}>{value}</div>
    </div>
  );
}

export function CreativeSlotCard(props: CreativeSlotCardProps) {
  const {
    id, name, poster, initials, isVideo, mediaLabel, formatLabel, versionLabel, newerVersion, sizes, sizesNote,
    provider, delivery, progress, metrics, metricsWindow, actions, footer, aside, ariaLabel,
  } = props;
  const tone = props.tone ?? delivery?.tone ?? 'quiet';
  const present = sizes?.filter((size) => size.present).length ?? 0;
  const ctr = metrics && metrics.impressions > 0 ? (metrics.clicks / metrics.impressions) * 100 : null;
  const completion = metrics && metrics.videoStarts > 0 ? (metrics.videoCompletions / metrics.videoStarts) * 100 : null;
  const inFlight = progress ? progress.status === 'queued' || progress.status === 'running' : false;
  return (
    <section id={id} className={`panel creative-slot-card creative-slot-card--${tone}`} aria-label={ariaLabel} data-slot-tone={tone}>
      <div className={`creative-slot-grid${aside !== undefined ? ' creative-slot-grid--draft' : ''}`}>
        <div className={`creative-slot-poster${provider === 'ttd' ? ' creative-slot-poster--wide' : ''}`} aria-hidden="true">
          {poster ? (
            poster.kind === 'video'
              ? <video src={poster.url} muted loop playsInline className="poster-media" />
              : <img src={poster.url} alt="" loading="lazy" className="poster-media" />
          ) : (
            <div className="creative-slot-initials">{initials}</div>
          )}
          {isVideo && (
            <span className="creative-slot-play">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
            </span>
          )}
          {poster && <span className="creative-slot-ratio mono">{poster.ratio}</span>}
        </div>

        <div className="creative-slot-identity">
          <h3 className="creative-slot-name">{name}</h3>
          <div className="creative-slot-tags">
            <span className="tag">{mediaLabel}</span>
            {formatLabel && <span className="tag">{formatLabel}</span>}
            <span className="tag tag-version">{versionLabel}</span>
            {newerVersion != null && <span className="tag creative-slot-tag-newer">v{newerVersion} approved · not yet rolled out</span>}
          </div>
          {sizes && (
            <div className="creative-slot-sizes">
              <span className="creative-slot-eyebrow">Sizes · {present} of {sizes.length}</span>
              <div className="creative-slot-chips">
                {sizes.map((size) => (
                  <span key={size.key} className={`tag creative-slot-chip${size.present ? ' is-present' : ' is-missing'}`}>
                    {size.present ? '✓ ' : ''}{size.label}{size.present ? '' : ' · missing'}
                  </span>
                ))}
              </div>
              {sizesNote && <div className="creative-slot-note">{sizesNote}</div>}
            </div>
          )}
          <div className="creative-slot-actions">{actions}</div>
        </div>

        {aside !== undefined ? (
          <div className="creative-slot-aside">{aside}</div>
        ) : (
        <>
        <div className="creative-slot-delivery">
          {progress && delivery ? (
            /* A change in flight or one that failed: the steps, with their
               times, stand in for the per-audience ad lines; the note is the
               delivery model's own sentence (leave the page / ask again). */
            <>
              <div className="creative-slot-headline" style={{ color: TONE_COLOR[delivery.tone] }}>
                {inFlight
                  ? <span className="creative-slot-spinner" aria-hidden="true" />
                  : <span className="creative-slot-dot" style={{ background: TONE_COLOR[delivery.tone] }} aria-hidden="true" />}
                {progress.headline}
                <span className="creative-slot-started mono tnum">started {fmtTimeSeconds(progress.startedAt)}</span>
              </div>
              <Steps progress={progress} />
              {delivery.note && <div className={`creative-slot-note${delivery.tone === 'failed' ? ' creative-slot-error' : ''}`}>{delivery.note}</div>}
            </>
          ) : delivery ? (
            <>
              <div className="creative-slot-headline" style={{ color: TONE_COLOR[delivery.tone] }}>
                <span className="creative-slot-dot" style={{ background: TONE_COLOR[delivery.tone] }} aria-hidden="true" />
                {delivery.headline}
              </div>
              {delivery.lines.length > 0 && (
                <dl className="creative-slot-lines">
                  {delivery.lines.map((line) => (
                    <div key={line.label} className="creative-slot-line">
                      <dt className="mono">{line.label}</dt>
                      <dd className="mono tnum">{line.value}</dd>
                    </div>
                  ))}
                </dl>
              )}
              {delivery.note && <div className="creative-slot-note">{delivery.note}</div>}
            </>
          ) : (
            <div className="creative-slot-note">Not published yet.</div>
          )}
        </div>

        <div className="creative-slot-metrics">
          {metricsWindow && <div className="creative-slot-eyebrow">{metricsWindow}</div>}
          {metrics && metrics.impressions > 0 ? (
            <>
              <div className="creative-slot-metric-row">
                <Metric label="Impressions" value={fmt.n(metrics.impressions).toUpperCase()} big />
                {provider === 'ttd'
                  ? <Metric label="Completed views" value={completion === null ? '—' : `${Math.round(completion)}%`} big />
                  : <Metric label="Clicks" value={metrics.clicks.toLocaleString()} big />}
              </div>
              <div className="creative-slot-metric-row">
                {provider === 'ttd'
                  ? <Metric label="Gross spend" value={fmt.$full(metrics.spendCents / 100)} />
                  : <Metric label="CTR" value={ctr === null ? '—' : `${ctr.toFixed(2)}%`} />}
                {provider === 'ttd'
                  ? <Metric label="CPM" value={metrics.impressions > 0 ? fmt.cur((metrics.spendCents / 100) / (metrics.impressions / 1000)) : '—'} />
                  : <Metric label="Gross spend" value={fmt.$full(metrics.spendCents / 100)} />}
              </div>
              {metrics.series.length > 1 && (
                <div className="creative-slot-spark">
                  <Sparkline data={metrics.series} w={180} h={26} color="var(--accent)" />
                </div>
              )}
              {metrics.days > 0 && metrics.days < 3 && (
                <div className="creative-slot-note">{metrics.days} day{metrics.days === 1 ? '' : 's'} of delivery · too early to compare</div>
              )}
            </>
          ) : (
            <div className="creative-slot-note">{metrics ? 'No delivery reported yet' : 'Delivery not read yet'}</div>
          )}
        </div>
        </>
        )}
      </div>
      {footer && <div className="creative-slot-footer">{footer}</div>}
    </section>
  );
}
